import React from 'react';
import {
    CheckCircle2,
    AlertCircle,
    Zap
} from 'lucide-react';
import type { AnalyticsKPIs, AnalyticsRealTimeActivity, AnalyticsKeyLocation } from '../../services/api';
import './RightSidebar.css';

interface KPIsProps {
    data?: AnalyticsKPIs;
    isLoading?: boolean;
}

interface LocationsProps {
    data?: AnalyticsKeyLocation[];
}

interface AlertsProps {
    data?: AnalyticsRealTimeActivity[];
}

export const RealTimeMonitoringKPI: React.FC<KPIsProps> = ({ data, isLoading }) => {
    if (isLoading && !data) {
        return (
            <div className="right-widget skeleton-widget">
                <h3 className="widget-title">Real-Time Monitoring</h3>
                <div className="monitoring-rows">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="monitoring-row skeleton-row">
                            <div className="skeleton-label"></div>
                            <div className="skeleton-value"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    const stats = [
        { label: 'Active Cards', value: data?.userIssuanceRecords?.toLocaleString() || '0', color: '#10b981' },
        { label: 'Card Requests', value: data?.cardRequests?.toLocaleString() || '0', color: '#f59e0b' },
        { label: 'Flagged Transactions', value: data?.flaggedTransactions?.toLocaleString() || '0', color: '#ef4444' },
    ];

    return (
        <div className="right-widget">
            <h3 className="widget-title">Real-Time Monitoring</h3>
            <div className="monitoring-rows">
                {stats.map((stat) => (
                    <div key={stat.label} className="monitoring-row">
                        <div className="monitoring-label-group">
                            <div className="dot" style={{ backgroundColor: stat.color }} />
                            <span>{stat.label}</span>
                        </div>
                        <span className="monitoring-value">{stat.value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const RecentAlerts: React.FC<AlertsProps> = ({ data = [] }) => {
    return (
        <div className="right-widget">
            <h3 className="widget-title">Recent Alerts</h3>
            <div className="alerts-list">
                {data.map((alert, i) => {
                    const AlertIcon = alert.type === 'success' ? CheckCircle2 :
                        alert.type === 'warning' ? AlertCircle :
                            alert.type === 'error' ? AlertCircle : Zap;

                    const color = alert.type === 'success' ? '#10b981' :
                        alert.type === 'warning' ? '#f59e0b' :
                            alert.type === 'error' ? '#ef4444' : '#3b82f6';
                    return (
                        <div key={i} className="alert-item">
                            <div className="alert-icon-circle" style={{ backgroundColor: `${color}10`, color: color }}>
                                <AlertIcon size={14} />
                            </div>
                            <div className="alert-info">
                                <span className="alert-title">{alert.title}</span>
                                <p className="alert-desc">{alert.description}</p>
                                <span className="alert-time">{alert.time}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export const KeyLocations: React.FC<LocationsProps> = ({ data = [] }) => {
    // Find absolute max to calculate percentage width
    const maxCount = Math.max(...(data.map(d => d.count)), 1);

    return (
        <div className="right-widget">
            <h3 className="widget-title">Top Locations Today</h3>
            <div className="locations-list-mini">
                {data.map((loc) => {
                    const percentage = (loc.count / maxCount) * 100;
                    return (
                        <div key={loc.city} className="mini-loc-item">
                            <div className="mini-loc-header">
                                <span className="mini-loc-name">{loc.city}</span>
                                <span className="mini-loc-val">{loc.count.toLocaleString()} transactions</span>
                            </div>
                            <div className="mini-loc-bar-bg">
                                <div className="mini-loc-bar" style={{ width: `${percentage}%` }} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export const QuickActionsRight: React.FC = () => {
    const handleGenerateReport = async () => {
        try {
            const { apiService } = await import('../../services/api');
            const blob = await apiService.generateMonthlyReport();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Monthly_Report_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to generate report:', error);
            alert('Failed to generate report. Please try again.');
        }
    };

    const handleExportData = async () => {
        try {
            const { apiService } = await import('../../services/api');
            const blob = await apiService.exportTransactionsCsv();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Transactions_Export_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to export data:', error);
            alert('Failed to export data. Please try again.');
        }
    };

    const handleAddCompany = () => {
        // Find the Dashboard component's state or simply dispatch a custom event
        // Alternatively we can use window.location if router context is not passed, 
        // but the best way is to dispatch an event that Dashboard listens to, or just click the sidebar link.
        const companyLink = document.querySelector('.sidebar-item[title="Go to Companies"]') || 
                            document.querySelector('.sidebar-item:nth-child(2)'); // Company is usually 2nd or has specific class
        if (companyLink) {
            (companyLink as HTMLElement).click();
        } else {
            // Fallback: reload to companies hash/state if we use hash routing or window navigation
            // Since Dashboard handles activeMenuItem locally, we dispatch a custom event to change it.
            window.dispatchEvent(new CustomEvent('navigate-dashboard', { detail: 'companies' }));
        }
    };

    const actions = [
        { label: 'Generate Monthly Report', color: '#ff6b3510', textColor: '#ff6b35', onClick: handleGenerateReport },
        { label: 'Export Transaction Data', color: '#3b82f610', textColor: '#3b82f6', onClick: handleExportData },
        { label: 'Add New Company', color: '#10b98110', textColor: '#10b981', onClick: handleAddCompany },
    ];

    return (
        <div className="right-widget">
            <h3 className="widget-title">Quick Actions</h3>
            <div className="action-buttons-stack">
                {actions.map((action) => (
                    <button
                        key={action.label}
                        className="stack-action-btn"
                        style={{ backgroundColor: action.color, color: action.textColor }}
                        onClick={action.onClick}
                    >
                        {action.label}
                    </button>
                ))}
            </div>
        </div>
    );
};

const RightSidebar: React.FC = () => {
    return (
        <aside className="dashboard-right-sidebar">
            <RealTimeMonitoringKPI />
            <RecentAlerts />
            <KeyLocations />
            <QuickActionsRight />
        </aside>
    );
};

export default RightSidebar;
